"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createWindowSwitcherOptions = exports.WindowSwitcher = void 0;
var metrics_engine_1 = require("@tableau/metrics-engine");
var widgets_1 = require("@tableau/widgets");
var React = require("react");
var typestyle_1 = require("typestyle");
var Localize_1 = require("../../gen-src/Localize");
/**
 * The WindowSwitcher, introduced in 2022.2 with editing, is a component on the detail screen rendered
 * above the timeseries. It allows the user to preview the metric with a different date window without
 * editing the metric configuration.
 *
 * It should be displayed as long as there is more than one data point, but its interactivity should be
 * disabled while the user is editing the configuration so that the "WindowSwitcher selection" and
 * "Configuration selection" are not out of sync. Its options should be updated as the user changes the
 * authored window, and should be re-enabled with the authored window as the selected window when the user
 * is done editing.
 */
var WindowSwitcher = /** @class */ (function (_super) {
    __extends(WindowSwitcher, _super);
    function WindowSwitcher(props) {
        var _this = _super.call(this, props) || this;
        _this.handleKeyDown = function (event) {
            var tablist = event.currentTarget;
            var buttonElems = Array.from(tablist.querySelectorAll('button:enabled'));
            var currentButtonIndex = buttonElems.indexOf(event.target);
            var numOptions = buttonElems.length;
            switch (event.key) {
                case 'ArrowRight':
                    buttonElems[(currentButtonIndex + 1) % numOptions].focus();
                    break;
                case 'ArrowLeft':
                    buttonElems[(numOptions + currentButtonIndex - 1) % numOptions].focus();
                    break;
                case 'Home':
                    buttonElems[0].focus();
                    break;
                case 'End':
                    buttonElems[numOptions - 1].focus();
                    break;
                default:
                    return;
            }
            event.preventDefault();
            event.stopPropagation();
        };
        return _this;
    }
    WindowSwitcher.prototype.render = function () {
        var _this = this;
        var _a = this.props, options = _a.options, selectedWindow = _a.selectedWindow;
        var selectedOptionIndex = selectedWindow
            ? options.findIndex(function (dateWindow) { return (0, metrics_engine_1.areWindowsEqual)(dateWindow, selectedWindow); })
            : -1;
        var optionsToDisplay = options.map(function (dateWindow, i) {
            var isSelected = !_this.props.disabled && i === selectedOptionIndex;
            // If for some reason the selectedOptionIndex is -1 (the authoredWindow couldn't be found in options),
            // we don't want any option to be selected (since it could be out of sync with the time series), but
            // we also need the user to be able to tab to the window switcher.
            var shouldBeTabStop = selectedOptionIndex === -1 && i === 0;
            var label = (0, metrics_engine_1.getLabelForWindowPickerOption)(dateWindow);
            var ariaLabel = (0, metrics_engine_1.isSpecialWindow)(dateWindow) || (0, metrics_engine_1.isDefinedStartWindow)(dateWindow) ? label : (0, metrics_engine_1.displayStringFromWindow)(dateWindow);
            return (React.createElement(React.Fragment, { key: "option-" + i },
                React.createElement("button", { onClick: function () { return _this.props.onWindowChange(dateWindow); }, className: OptionStyle, "data-tb-test-id": "window-switcher-option-" + i + (isSelected ? '-selected' : ''), role: 'tab', "aria-selected": isSelected, tabIndex: isSelected || shouldBeTabStop ? 0 : -1, disabled: _this.props.disabled, "aria-label": ariaLabel, "aria-controls": _this.props.timeSeriesId }, label),
                i !== options.length - 1 && React.createElement("span", { style: SeparatorStyle })));
        });
        return (React.createElement("div", { role: 'tablist', style: OptionContainerStyle, onKeyDown: this.handleKeyDown, "aria-describedby": 'current-legend', "aria-label": Localize_1.Messages.ariaWindowSwitcher() }, optionsToDisplay));
    };
    return WindowSwitcher;
}(React.PureComponent));
exports.WindowSwitcher = WindowSwitcher;
/**
 * Creates a list of DateWindows that the user may select between on the metric detail page
 * via the WindowSwitcher component. This function is used by MetricDetailView and eventually
 * passed through props to the WindowSwitcher.
 *
 * @param dateTrunc the DateTrunc of the metric
 * @param authoredWindow the window, if it exists, that the metric was authored with
 * @returns an ordered list of DateWindows to be rendered by the WindowSwitcher
 */
var createWindowSwitcherOptions = function (dateTrunc, authoredWindow) {
    var presetOptions = (0, metrics_engine_1.dateWindowOptions)(dateTrunc);
    var isAuthoredWindowCustom = authoredWindow !== undefined &&
        !presetOptions.some(function (dateWindow) { return (0, metrics_engine_1.areWindowsEqual)(dateWindow, authoredWindow); });
    return isAuthoredWindowCustom ? [authoredWindow].concat(presetOptions) : presetOptions;
};
exports.createWindowSwitcherOptions = createWindowSwitcherOptions;
// While these styles are very similar to TabsWidget's, that widget can't be used
// because of performance reasons.
var SHADOW_SIZE = 4;
var PADDING_SIZE = 5;
var OptionContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    marginTop: widgets_1.TabStyles.Spacing.ExtraLargeSpace + "px",
    // the leftmost option needs to be flush with the BAN/delta text/etc, but it also has
    // margin and padding, so we need negative margin here to keep things aligned
    marginLeft: "-" + (SHADOW_SIZE + PADDING_SIZE) + "px",
    minHeight: '39px',
};
var OptionStyle = (0, typestyle_1.style)({
    whiteSpace: 'nowrap',
    color: widgets_1.TabStyles.Typography.Color.Primary,
    fontSize: '14px',
    fontFamily: 'inherit',
    fontWeight: 'bold',
    background: 'none',
    borderWidth: '2px 1px 2px 1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: '2px',
    padding: PADDING_SIZE + "px",
    // Margin is needed to keep the focus style shadow to be visible
    // above and below as well
    margin: SHADOW_SIZE + "px",
    $nest: {
        '&:focus-visible': {
            border: "1px solid " + widgets_1.TabStyles.Colors.Focus,
            boxShadow: "0 0 " + SHADOW_SIZE + "px 0 " + widgets_1.TabStyles.Colors.Focus,
            outline: 0,
            // plus one to offset the smaller border bottom width
            margin: SHADOW_SIZE + 1 + "px " + SHADOW_SIZE + "px",
        },
        '&:hover:enabled': {
            borderBottom: "2px solid " + widgets_1.TabStyles.Colors.L7,
        },
        '&[aria-selected="true"]': {
            color: '#1a699e',
            borderBottom: "2px solid #1a699e",
            $nest: {
                '&:hover, &:focus-visible': {
                    borderBottom: "2px solid #1a699e",
                    marginBottom: SHADOW_SIZE + "px",
                },
            },
        },
        '&:disabled': {
            color: widgets_1.TabStyles.Typography.Color.Disabled,
            borderColor: 'transparent',
        },
    },
});
var SeparatorStyle = {
    // 15 is the space between the option text and the separator, but we also
    // need to account for the padding and margin it has
    margin: "0 " + (15 - PADDING_SIZE - SHADOW_SIZE) + "px",
    borderLeft: "1px solid " + widgets_1.TabStyles.Colors.L3,
    height: '24px',
};
//# sourceMappingURL=WindowSwitcher.js.map